import { Component, HostListener, inject, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterLink } from '@angular/router';
import { Subject, debounceTime, distinctUntilChanged } from 'rxjs';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import {
  faMagnifyingGlass,
  faEllipsisVertical,
  faBox,
  faPlus,
  faPen,
  faTrash,
  faSort,
  faSortUp,
  faSortDown,
  faEuroSign,
} from '@fortawesome/pro-solid-svg-icons';
import { ProductsService } from '../../../core/services/products.service';
import { Product } from '../../../core/models/product.model';
import { PopupConfirmService } from '../../../shared/services/popup-confirm.service';
import { PopupInfoService } from '../../../shared/services/popup-info.service';

@Component({
  selector: 'app-products-list-page',
  standalone: true,
  imports: [CommonModule, RouterLink, FontAwesomeModule],
  templateUrl: './products-list-page.html',
  styleUrls: ['./products-list-page.scss'],
})
export class ProductsListPage implements OnInit, OnDestroy {
  private readonly productsService = inject(ProductsService);
  private readonly popupConfirmService = inject(PopupConfirmService);
  private readonly popupInfoService = inject(PopupInfoService);
  private searchSubject = new Subject<string>();
  searchValue: string = '';

  faMagnifyingGlass = faMagnifyingGlass;
  faEllipsisVertical = faEllipsisVertical;
  faBox = faBox;
  faPlus = faPlus;
  faPen = faPen;
  faTrash = faTrash;
  faSort = faSort;
  faSortUp = faSortUp;
  faSortDown = faSortDown;
  faEuroSign = faEuroSign;

  products: Product[] = [];
  filteredProducts: Product[] = [];
  isLoading = false;
  error: string | null = null;
  openMenuId: number | null = null;
  menuPosition: { top: number; left: number } | null = null;
  private readonly menuWidth = 192; // w-48
  private readonly menuHeight = 100; // altura aproximada del menú (2 opciones)

  // Sort
  sortBy: keyof Product | null = null;
  sortDirection: 'asc' | 'desc' | null = null;

  ngOnInit(): void {
    this.loadProducts();

    this.searchSubject.pipe(debounceTime(300), distinctUntilChanged()).subscribe((search) => {
      this.searchValue = search;
      this.applyFilters();
    });
  }

  ngOnDestroy(): void {
    this.searchSubject.complete();
  }

  loadProducts(): void {
    this.isLoading = true;
    this.error = null;

    this.productsService.list().subscribe({
      next: (products) => {
        this.products = products;
        this.applyFilters();
        this.isLoading = false;
      },
      error: (err) => {
        this.error = 'Error al cargar los productos';
        this.isLoading = false;
        console.error('Error loading products:', err);
      },
    });
  }

  applyFilters(): void {
    let result = [...this.products];

    if (this.searchValue) {
      const search = this.searchValue.toLowerCase();
      result = result.filter(
        (p) =>
          p.name.toLowerCase().includes(search) ||
          p.category.toLowerCase().includes(search) ||
          p.description.toLowerCase().includes(search)
      );
    }

    if (this.sortBy) {
      result.sort((a, b) => {
        const valA = a[this.sortBy!];
        const valB = b[this.sortBy!];

        if (valA === valB) return 0;

        let comparison = 0;
        if (typeof valA === 'string' && typeof valB === 'string') {
          comparison = valA.localeCompare(valB);
        } else {
          comparison = (valA as any) > (valB as any) ? 1 : -1;
        }

        return this.sortDirection === 'desc' ? -comparison : comparison;
      });
    }

    this.filteredProducts = result;
  }

  onSearch(search: string): void {
    this.searchSubject.next(search);
  }

  onSort(column: keyof Product): void {
    if (this.sortBy === column) {
      this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortBy = column;
      this.sortDirection = 'asc';
    }
    this.applyFilters();
  }

  getSortIcon(column: keyof Product) {
    if (this.sortBy !== column) return this.faSort;
    return this.sortDirection === 'asc' ? this.faSortUp : this.faSortDown;
  }

  deleteProduct(id: number): void {
    const product = this.products.find((p) => p.id === id);
    const productName = product?.name || 'este producto';

    this.popupConfirmService.showDeleteConfirm(productName, () => {
      this.productsService.delete(id).subscribe({
        next: () => {
          this.popupInfoService.showSuccess(`El producto "${productName}" ha sido eliminado.`);
          this.loadProducts();
        },
        error: (err) => {
          this.error = 'Error al eliminar el producto';
          console.error('Error deleting product:', err);
        },
      });
    });
  }

  toggleMenu(productId: number, event: Event): void {
    event.stopPropagation();

    if (this.openMenuId === productId) {
      this.closeMenu();
      return;
    }

    const button = event.currentTarget as HTMLElement;
    const rect = button.getBoundingClientRect();
    const opensUpward = rect.bottom + 8 + this.menuHeight > window.innerHeight;

    this.menuPosition = {
      top: opensUpward ? Math.max(8, rect.top - 8 - this.menuHeight) : rect.bottom + 8,
      left: Math.max(8, rect.right - this.menuWidth),
    };
    this.openMenuId = productId;
  }

  closeMenu(): void {
    this.openMenuId = null;
    this.menuPosition = null;
  }

  @HostListener('window:scroll')
  onWindowScroll(): void {
    // El menú de acciones está posicionado en fixed respecto al botón que lo abrió;
    // al hacer scroll dejaría de estar alineado, así que lo cerramos.
    this.closeMenu();
  }

  @HostListener('document:click')
  onDocumentClick(): void {
    this.closeMenu();
  }
}
